import { mergeAttributes, Node } from '@tiptap/core';

import { PANEL_MENTION_NODE } from '../../lib/composer-document';
import { MENTION_TRIGGER } from '../../lib/panel-mentions';

/**
 * A dashboard panel named inside a message.
 *
 * The node is an atom, so it has no editable interior: one Backspace removes all of it, a selection
 * cannot take half of it, and a paste cannot land inside it. That is the property a textarea could
 * only approximate, and the reason the composer holds a document instead of a string.
 *
 * `id` is the record. `label` is what the reader sees, and what the message serializes to.
 */
export const PanelMentionNode = Node.create({
  name: PANEL_MENTION_NODE,
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-panel-id'),
        renderHTML: (attributes) => ({ 'data-panel-id': attributes['id'] as string }),
      },
      label: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-panel-label'),
        renderHTML: (attributes) => ({ 'data-panel-label': attributes['label'] as string }),
      },
    };
  },

  parseHTML() {
    return [{ tag: `span[data-panel-id]` }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        class:
          'rounded bg-muted px-1 py-0.5 text-[0.8125rem] text-brand whitespace-nowrap ' +
          'data-[selected=true]:ring-2 data-[selected=true]:ring-brand/40',
        'data-testid': `composer-mention-${(node.attrs['id'] as string | null) ?? 'unknown'}`,
      }),
      `${MENTION_TRIGGER}${(node.attrs['label'] as string | null) ?? ''}`,
    ];
  },

  /** Used by the plain-text serializer, and by anything that reads the document as a string. */
  renderText({ node }) {
    return `${MENTION_TRIGGER}${(node.attrs['label'] as string | null) ?? ''}`;
  },
});
